import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { labApi } from "@/api/lab";
import { patientApi } from "@/api/patient";
import { doctorApi } from "@/api/doctor";
import { Patient } from "@/types/types";
import {
    DoctorSearchableSelect,
    EXTERNAL_DOCTOR_ID,
} from "@/components/common/DoctorSearchableSelect";
import { PatientSearchableSelect } from "@/components/common/PatientSearchableSelect";




interface CreateLabOrderDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export default function CreateLabOrderDialog({
    open,
    onOpenChange,
}: CreateLabOrderDialogProps) {
    const [batchOrderPatient, setBatchOrderPatient] = useState<string>('');
    const [selectedLabTests, setSelectedLabTests] = useState<string[]>([]);
    const [orderNotes, setOrderNotes] = useState<string>('');
    const [urgentOrder, setUrgentOrder] = useState<boolean>(false);
    const [labTestSearchQuery, setLabTestSearchQuery] = useState('');
    const [processingType, setProcessingType] = useState<"INTERNAL" | "EXTERNAL">("INTERNAL");
    const [selectedExternalVendorId, setSelectedExternalVendorId] = useState<string>("");
    const [externalCostByLabTestId, setExternalCostByLabTestId] = useState<Record<string, string>>({});
    const [recommendedDoctorValue, setRecommendedDoctorValue] = useState("");

    const { data: patients } = useQuery<Patient[]>({
        queryKey: ['hospital-patients'],
        queryFn: async () => {
            const response = await patientApi.getAllPatients();
            return response;
        },
    });

    const { data: labTests } = useQuery<any>({
        queryKey: ['lab-tests'],
        queryFn: async () => {
            const response = await labApi.getLabTests();
            return response.data?.data;
        },
    });

    const { data: externalVendors = [] } = useQuery<any[]>({
        queryKey: ["lab-external-vendors"],
        queryFn: async () => {
            const res = await labApi.listExternalVendors();
            return res.data?.data ?? [];
        },
        staleTime: 300000,
    });

    const { data: doctors = [] } = useQuery({
        queryKey: ["hospital-doctors"],
        queryFn: () => doctorApi.getDoctorsByHospital(),
        staleTime: 300000,
    });

    const queryClient = useQueryClient();

    const createExternalLabOrderMutation = useMutation({
        mutationFn: async (orderData: {
            patientId: string;
            labTestIds: string[];
            notes?: string;
            urgentOrder: boolean;
            processingType: "INTERNAL" | "EXTERNAL";
            externalVendorId?: string;
            externalCosts?: Record<string, number>;
            recommendedDoctorId?: string | null;
            externalDoctor?: boolean;
        }) => {
            const response = await labApi.createExternalLabOrder(orderData);
            return response.data?.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['all-test-appointments'] });
            queryClient.invalidateQueries({ queryKey: ['external-lab-orders'] });
            onOpenChange(false);
            toast.success('Lab order created successfully');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Failed to create lab order');
        },
    });
    const filteredLabTests = (labTests ?? []).filter((test: any) => {
        const q = labTestSearchQuery.trim().toLowerCase();
        if (!q) return true;
        const name = String(test?.name ?? '').toLowerCase();
        const code = String(test?.code ?? '').toLowerCase();
        const sampleType = String(test?.sampleType ?? '').toLowerCase();
        return name.includes(q) || code.includes(q) || sampleType.includes(q);
    });

    const selectedLabTestRows = (labTests ?? []).filter((t: any) => selectedLabTests.includes(t.id));
    const selectedPatientPayTotal = selectedLabTestRows.reduce(
        (sum: number, t: any) => sum + (Number(t?.charge) || 0),
        0
    );

    // Handler for lab test selection
    const handleLabTestSelection = (testId: string, checked: boolean) => {
        if (checked) {
            setSelectedLabTests(prev => [...prev, testId]);
        } else {
            setSelectedLabTests(prev => prev.filter(id => id !== testId));
        }
    };

    // Handler for creating lab order
    const handleCreateLabOrder = () => {
        if (!batchOrderPatient) {
            toast.error('Please select a patient');
            return;
        }
        if (selectedLabTests.length === 0) {
            toast.error('Please select at least one lab test');
            return;
        }
        if (!recommendedDoctorValue) {
            toast.error('Please select the recommending doctor');
            return;
        }

        if (processingType === "EXTERNAL") {
            if (!selectedExternalVendorId) {
                toast.error("Please select an external vendor");
                return;
            }
            for (const id of selectedLabTests) {
                const raw = externalCostByLabTestId[id];
                const val = raw === "" || raw === undefined ? NaN : Number(raw);
                if (!Number.isFinite(val) || val < 0) {
                    toast.error("Please enter valid hospital cost for each selected test");
                    return;
                }
            }
        }

        const externalCosts: Record<string, number> | undefined =
            processingType === "EXTERNAL"
                ? Object.fromEntries(selectedLabTests.map((id) => [id, Number(externalCostByLabTestId[id] || 0)]))
                : undefined;

        const externalDoctor = recommendedDoctorValue === EXTERNAL_DOCTOR_ID;

        createExternalLabOrderMutation.mutate({
            patientId: batchOrderPatient,
            labTestIds: selectedLabTests,
            notes: orderNotes || undefined,
            urgentOrder: urgentOrder,
            processingType,
            externalVendorId: processingType === "EXTERNAL" ? selectedExternalVendorId : undefined,
            externalCosts,
            recommendedDoctorId: externalDoctor ? null : recommendedDoctorValue,
            externalDoctor,
        });

        setBatchOrderPatient('');
        setSelectedLabTests([]);
        setOrderNotes('');
        setUrgentOrder(false);
        setLabTestSearchQuery('');
        setProcessingType("INTERNAL");
        setSelectedExternalVendorId("");
        setExternalCostByLabTestId({});
        setRecommendedDoctorValue("");


    };

    // Reset form when dialog closes
    const handleClose = () => {
        onOpenChange(false);
        setBatchOrderPatient('');
        setSelectedLabTests([]);
        setOrderNotes('');
        setUrgentOrder(false);
        setLabTestSearchQuery('');
        setProcessingType("INTERNAL");
        setSelectedExternalVendorId("");
        setExternalCostByLabTestId({});
        setRecommendedDoctorValue("");
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) handleClose();
            }}
        >
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Create Lab Order</DialogTitle>
                </DialogHeader>
                <div className="space-y-6">
                    {/* Internal / External */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-base font-semibold">Processing Type</Label>
                            <Select value={processingType} onValueChange={(v: any) => setProcessingType(v)}>
                                <SelectTrigger className="mt-2">
                                    <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="INTERNAL">Internal</SelectItem>
                                    <SelectItem value="EXTERNAL">External</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {processingType === "EXTERNAL" ? (
                            <div>
                                <Label className="text-base font-semibold">External Vendor</Label>
                                <Select value={selectedExternalVendorId} onValueChange={setSelectedExternalVendorId}>
                                    <SelectTrigger className="mt-2">
                                        <SelectValue placeholder="Select vendor" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {externalVendors.map((v: any) => (
                                            <SelectItem key={v.id} value={v.id}>
                                                {v.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        ) : (
                            <div />
                        )}
                    </div>

                    {/* Patient Selection */}
                    <div>
                        <Label className="text-base font-semibold">Select Patient</Label>
                        <div className="mt-2">
                            <PatientSearchableSelect
                                patients={patients ?? []}
                                value={batchOrderPatient}
                                onValueChange={setBatchOrderPatient}
                                placeholder="Search patients by name, ID or phone…"
                            />
                        </div>
                    </div>

                    {/* Recommending doctor */}
                    <div>
                        <Label className="text-base font-semibold">Recommending doctor</Label>
                        <div className="mt-2">
                            <DoctorSearchableSelect
                                doctors={doctors}
                                value={recommendedDoctorValue}
                                onValueChange={setRecommendedDoctorValue}
                                allowExternalDoctor
                                placeholder="Search and select doctor…"
                            />
                        </div>
                    </div>

                    {/* Lab Tests Selection */}
                    <div>
                        <Label className="text-base font-semibold">Select Lab Tests</Label>
                        <div className="space-y-2 mt-2">
                            <div className="relative">
                                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                                <Input
                                    placeholder="Search lab tests by name, code or sample..."
                                    value={labTestSearchQuery}
                                    onChange={(e) => setLabTestSearchQuery(e.target.value)}
                                    className="pl-8"
                                />
                            </div>
                        </div>
                        <div className="border rounded-md max-h-64 overflow-y-auto mt-2">
                            {filteredLabTests.length > 0 ? filteredLabTests.map((test: any) => (
                                <div key={test.id} className="p-3 border-b last:border-b-0 hover:bg-gray-50">
                                    <div className="flex items-center space-x-3">
                                        <input
                                            type="checkbox"
                                            id={`test-${test.id}`}
                                            checked={selectedLabTests.includes(test.id)}
                                            onChange={(e) => handleLabTestSelection(test.id, e.target.checked)}
                                            className="h-4 w-4 rounded border border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2"
                                        />
                                        <label htmlFor={`test-${test.id}`} className="flex-1 cursor-pointer">
                                            <div className="font-medium">{test.name}</div>
                                            <div className="text-sm text-gray-500">
                                                Code: {test.code} | Sample: {test.sampleType} | ₹{test.charge}
                                            </div>
                                            {test.description && (
                                                <div className="text-sm text-gray-600 mt-1">{test.description}</div>
                                            )}
                                        </label>
                                        {processingType === "EXTERNAL" && selectedLabTests.includes(test.id) && (
                                            <div className="w-40">
                                                <Label className="text-xs text-gray-500">Hospital cost (₹)</Label>
                                                <Input
                                                    inputMode="decimal"
                                                    placeholder="0"
                                                    value={externalCostByLabTestId[test.id] ?? ""}
                                                    onChange={(e) =>
                                                        setExternalCostByLabTestId((p) => ({ ...p, [test.id]: e.target.value }))
                                                    }
                                                    className="mt-1 text-right"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )) : (
                                <div className="p-3 text-sm text-gray-500">No lab tests found</div>
                            )}
                        </div>
                        {selectedLabTests.length > 0 && (
                            <div className="mt-3 space-y-2">
                                <div className="text-sm text-blue-600">
                                    {selectedLabTests.length} test(s) selected
                                </div>

                                <div className="border rounded-md bg-white">
                                    <div className="px-3 py-2 border-b bg-muted/30 text-sm font-medium flex items-center justify-between">
                                        <span>Patient payable summary</span>
                                        <span className="tabular-nums">₹{selectedPatientPayTotal.toFixed(2)}</span>
                                    </div>
                                    <div className="max-h-40 overflow-y-auto">
                                        {selectedLabTestRows.map((t: any) => (
                                            <div key={t.id} className="px-3 py-2 border-b last:border-b-0 flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <div className="font-medium truncate">{t.name}</div>
                                                    <div className="text-xs text-gray-500 truncate">
                                                        {t.code ? `Code: ${t.code} · ` : ""}
                                                        {t.sampleType ? `Sample: ${t.sampleType}` : ""}
                                                    </div>
                                                </div>
                                                <div className="shrink-0 tabular-nums text-sm font-medium">
                                                    ₹{(Number(t?.charge) || 0).toFixed(2)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Additional Options */}
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="orderNotes" className="text-base font-semibold">Notes (Optional)</Label>
                            <Textarea
                                id="orderNotes"
                                placeholder="Enter any notes for this lab order..."
                                value={orderNotes}
                                onChange={(e) => setOrderNotes(e.target.value)}
                                className="mt-1"
                            />
                        </div>
                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="urgentOrder"
                                checked={urgentOrder}
                                onChange={(e) => setUrgentOrder(e.target.checked)}
                                className="h-4 w-4 rounded border border-gray-300 text-red-600 focus:ring-red-500 focus:ring-2"
                            />
                            <Label htmlFor="urgentOrder" className="text-red-600 font-medium">
                                Mark as Urgent Order
                            </Label>
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateLabOrder}
                        disabled={createExternalLabOrderMutation.isPending || !batchOrderPatient || !recommendedDoctorValue || selectedLabTests.length === 0}
                        className="bg-blue-600 hover:bg-blue-700"
                    >
                        {createExternalLabOrderMutation.isPending ? 'Creating...' : 'Create Order'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
} 